/* eslint-disable react/prop-types */
/**
 * TenantDataCleanupCard — owner-only Settings → Platform card.
 * [Iter 290zp.13q]
 *
 * Surfaces foreign-tagged rows physically present inside the current
 * tenant's MongoDB and lets the owner purge them with a confirmed
 * two-step flow. JSON snapshot is taken automatically before delete.
 *
 * Backend: /api/admin/tenant-purge/{preview,apply,history}
 */
import { useState } from "react";
import {
  ShieldAlert,
  Database,
  Loader2,
  CheckCircle2,
  Trash2,
  RefreshCw,
  History,
} from "lucide-react";
import api, { formatErr } from "../lib/api";

export default function TenantDataCleanupCard() {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [confirmInput, setConfirmInput] = useState("");
  const [banner, setBanner] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyOpen, setHistoryOpen] = useState(false);
  const target = "hidroverse";

  const loadPreview = async () => {
    setLoading(true);
    setBanner(null);
    try {
      const { data } = await api.get(
        `/admin/tenant-purge/preview?target_tenant=${encodeURIComponent(target)}`,
      );
      setPreview(data);
      setConfirmInput("");
    } catch (e) {
      setBanner({ kind: "error", text: formatErr(e?.response?.data?.detail) || "Preview failed" });
    } finally {
      setLoading(false);
    }
  };

  const loadHistory = async () => {
    try {
      const { data } = await api.get("/admin/tenant-purge/history?limit=10");
      setHistory(data?.events || []);
    } catch {
      setHistory([]);
    }
  };

  const apply = async () => {
    if (!preview || preview.total === 0) return;
    setApplying(true);
    setBanner(null);
    try {
      const expected = `DELETE ${preview.total} ROWS`;
      const { data } = await api.post(
        `/admin/tenant-purge/apply?target_tenant=${encodeURIComponent(target)}`,
        { confirm_token: expected },
      );
      setBanner({
        kind: "ok",
        text: `Purged ${data?.deleted ?? 0} rows. Snapshot: ${data?.snapshot_path || "n/a"}`,
      });
      setPreview(null);
      setConfirmInput("");
      await loadHistory();
    } catch (e) {
      setBanner({ kind: "error", text: formatErr(e?.response?.data?.detail) || "Purge failed" });
    } finally {
      setApplying(false);
    }
  };

  const expectedConfirm = preview ? `DELETE ${preview.total} ROWS` : "";
  const canApply =
    preview && preview.total > 0 && confirmInput.trim() === expectedConfirm;

  return (
    <div className="hv-card p-5" data-testid="tenant-data-cleanup-card">
      <div className="flex items-start gap-3 mb-3">
        <div className="w-10 h-10 rounded-md bg-rose-500/15 border border-rose-500/40 flex items-center justify-center">
          <ShieldAlert className="w-5 h-5 text-rose-300" />
        </div>
        <div className="min-w-0 flex-1">
          <h2 className="font-display text-2xl font-bold">Tenant data cleanup</h2>
          <p className="text-xs text-[var(--hv-text-muted)] max-w-2xl">
            Scans your tenant database for rows whose <code>tenant_id</code> does not match
            this tenant (legacy / cross-tenant bleed). Foreign-tagged rows are filtered from
            normal views by the safety net but still consume storage and can leak through any
            raw query. This tool deletes them — a JSON snapshot is taken before delete.
          </p>
        </div>
      </div>

      {/* Banner */}
      {banner && (
        <div
          className={`text-sm rounded-md p-2 mb-3 ${
            banner.kind === "ok"
              ? "bg-emerald-500/10 border border-emerald-500/40 text-emerald-300"
              : "bg-rose-500/10 border border-rose-500/40 text-rose-300"
          }`}
          data-testid="cleanup-banner"
        >
          {banner.text}
        </div>
      )}

      {/* Step 1 — preview */}
      <div className="rounded-md border border-[var(--hv-border)] p-3 mb-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="text-xs uppercase tracking-wider text-[var(--hv-text-muted)]">Step 1</div>
            <div className="font-semibold">Scan for foreign rows</div>
          </div>
          <button
            type="button"
            onClick={loadPreview}
            disabled={loading}
            className="hv-btn hv-btn-ghost text-sm"
            data-testid="cleanup-preview-btn"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            {preview ? "Refresh preview" : "Run preview"}
          </button>
        </div>

        {preview && (
          <div className="mt-3" data-testid="cleanup-preview-result">
            <div className="text-sm text-[var(--hv-text-secondary)] mb-2">
              Target DB: <code className="text-[#1fb6c1]">{preview.db_name}</code> · scanned at{" "}
              <span className="text-[var(--hv-text-muted)]">
                {new Date(preview.timestamp).toLocaleString()}
              </span>
            </div>
            {preview.total === 0 ? (
              <div className="inline-flex items-center gap-2 text-sm text-emerald-300">
                <CheckCircle2 className="w-4 h-4" /> No foreign-tagged rows. Database is clean.
              </div>
            ) : (
              <>
                <div className="text-sm mb-2">
                  <strong className="text-rose-300">{preview.total}</strong> rows will be deleted.
                </div>
                <div className="overflow-x-auto">
                  <table className="text-xs w-full" data-testid="cleanup-preview-table">
                    <thead className="text-[var(--hv-text-muted)]">
                      <tr>
                        <th className="text-left py-1 pr-3">Collection</th>
                        <th className="text-right py-1 pr-3">Count</th>
                        <th className="text-left py-1">Tags found</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(preview.per_collection || [])
                        .filter((s) => s.total > 0)
                        .map((s) => (
                          <tr key={s.collection} className="border-t border-[var(--hv-border)]">
                            <td className="py-1 pr-3 font-mono">{s.collection}</td>
                            <td className="py-1 pr-3 text-right">{s.total}</td>
                            <td className="py-1">
                              {(s.tags || [])
                                .map(
                                  (t) =>
                                    `${
                                      t.tenant_id === null
                                        ? "null"
                                        : t.tenant_id === ""
                                        ? '""'
                                        : `"${t.tenant_id}"`
                                    } (${t.count})`,
                                )
                                .join(", ")}
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Step 2 — apply */}
      {preview && preview.total > 0 && (
        <div className="rounded-md border border-rose-500/30 bg-rose-500/5 p-3 mb-3">
          <div className="text-xs uppercase tracking-wider text-[var(--hv-text-muted)]">Step 2</div>
          <div className="font-semibold mb-2">Backup & purge</div>
          <div className="text-xs text-[var(--hv-text-secondary)] mb-3">
            Snapshot will be written to{" "}
            <code>/app/backend/data/cleanup-backups/{preview.target_tenant}-&lt;timestamp&gt;.jsonl</code>{" "}
            before any delete. Type the phrase below exactly to enable the purge button.
          </div>
          <div className="text-xs mb-1 text-[var(--hv-text-muted)]">
            Type: <code className="text-rose-300">{expectedConfirm}</code>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <input
              type="text"
              value={confirmInput}
              onChange={(e) => setConfirmInput(e.target.value)}
              placeholder={expectedConfirm}
              className="hv-input text-sm font-mono flex-1 min-w-[200px]"
              data-testid="cleanup-confirm-input"
            />
            <button
              type="button"
              disabled={!canApply || applying}
              onClick={apply}
              className="hv-btn hv-btn-danger text-sm disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="cleanup-apply-btn"
            >
              {applying ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
              Purge {preview.total} rows
            </button>
          </div>
        </div>
      )}

      {/* History */}
      <details
        open={historyOpen}
        onToggle={(e) => {
          const open = e.currentTarget.open;
          setHistoryOpen(open);
          if (open) loadHistory();
        }}
        className="text-xs"
      >
        <summary className="cursor-pointer inline-flex items-center gap-1 text-[var(--hv-text-muted)] hover:text-white">
          <History className="w-3 h-3" /> History ({history.length})
        </summary>
        <div className="mt-2 space-y-1" data-testid="cleanup-history-list">
          {history.length === 0 && (
            <div className="text-[var(--hv-text-muted)]">No prior purges.</div>
          )}
          {history.map((h, i) => (
            <div key={i} className="border-t border-[var(--hv-border)] py-1">
              <span className="text-[var(--hv-text-muted)]">{h.timestamp}</span>
              {" — "}
              <strong>{h.deleted}</strong> rows by <code>{h.actor}</code>
              {h.snapshot_path && (
                <div className="text-[var(--hv-text-muted)] truncate">
                  <Database className="w-3 h-3 inline" /> {h.snapshot_path}
                </div>
              )}
            </div>
          ))}
        </div>
      </details>
    </div>
  );
}
